import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert } from '../components/shared/Alert';
import { LoadingSpinner } from '../components/shared/LoadingSpinner';
import { TruckIcon, Mail, ArrowLeft, CheckCircle } from 'lucide-react';
import { authApi } from '../services/api';
import { logger } from '../utils/logger';
import '../styles/modern.css';

export const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    setLoading(true);

    try {
      await authApi.forgotPassword(email);
      setSuccess(true);
    } catch (err: unknown) {
      logger.error('Failed to send reset link:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to send reset link';
      // Still show success to prevent email enumeration
      if (errorMessage.includes('429') || errorMessage.includes('Too many')) {
        setError('Too many password reset requests. Please try again later.');
        setSuccess(false);
      } else {
        // Show success regardless to prevent email enumeration
        setSuccess(true);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" style={{ background: 'linear-gradient(135deg, #f9fafb 0%, #e5e7eb 100%)' }}>
      {/* Left Side - Branding & Imagery */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden" style={{
        background: 'linear-gradient(135deg, #1e40af 0%, #2563eb 100%)',
        position: 'relative'
      }}>
        {/* Geometric Pattern Background */}
        <div style={{
          position: 'absolute',
          inset: 0,
          opacity: 0.1,
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }} />

        {/* Content Container */}
        <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
          {/* Logo & Brand */}
          <div className="flex items-center gap-3">
            <div style={{
              background: 'rgba(255, 255, 255, 0.15)',
              padding: '12px',
              borderRadius: '12px',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.2)'
            }}>
              <TruckIcon className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Transportation Safety</h1>
              <p className="text-blue-100 text-sm">Platform</p>
            </div>
          </div>

          {/* Value Proposition */}
          <div className="space-y-8">
            <div>
              <h2 className="text-4xl font-bold mb-4 leading-tight">
                Account Recovery
              </h2>
              <p className="text-xl text-blue-100">
                Don't worry, we'll help you get back into your account quickly and securely.
              </p>
            </div>

            {/* Security Notice */}
            <div style={{
              background: 'rgba(255, 255, 255, 0.1)',
              padding: '20px',
              borderRadius: '12px',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.2)'
            }}>
              <p className="text-sm text-blue-100 mb-2">Security Information</p>
              <ul className="text-sm text-white space-y-2">
                <li>Password reset links expire after 1 hour</li>
                <li>You'll receive a confirmation email after reset</li>
                <li>Contact support if you don't have access to your email</li>
              </ul>
            </div>
          </div>

          {/* Footer */}
          <div className="text-sm text-blue-100">
            {new Date().getFullYear()} Transportation Safety Platform. All rights reserved.
          </div>
        </div>
      </div>

      {/* Right Side - Reset Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden mb-8 text-center">
            <div className="flex justify-center items-center gap-3 mb-2">
              <div style={{
                background: 'var(--color-primary)',
                padding: '10px',
                borderRadius: '10px'
              }}>
                <TruckIcon className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-2xl font-bold" style={{ color: 'var(--color-gray-900)' }}>
                Transportation Safety
              </h1>
            </div>
          </div>

          {/* Card */}
          <div style={{
            background: 'white',
            borderRadius: 'var(--radius-xl)',
            padding: '48px',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.08)',
            border: '1px solid var(--color-gray-200)'
          }} className="fade-in-up">
            {/* Back Link */}
            <Link
              to="/login"
              className="inline-flex items-center gap-2 text-sm mb-6"
              style={{ color: 'var(--color-gray-600)' }}
            >
              <ArrowLeft className="w-4 h-4" />
              Back to login
            </Link>

            {success ? (
              /* Success State */
              <div className="text-center">
                <div style={{
                  width: '64px',
                  height: '64px',
                  background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 24px'
                }}>
                  <CheckCircle className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--color-gray-900)' }}>
                  Check your email
                </h2>
                <p className="text-muted mb-6">
                  If an account exists with <strong>{email}</strong>, we've sent a password reset link that will expire in 1 hour.
                </p>
                <p className="text-sm text-muted mb-8">
                  Didn't receive the email? Check your spam folder or try again with a different email address.
                </p>
                <button
                  onClick={() => {
                    setSuccess(false);
                    setEmail('');
                  }}
                  style={{
                    padding: '10px 24px',
                    fontSize: '14px',
                    fontWeight: 500,
                    color: 'var(--color-primary)',
                    textDecoration: 'none',
                    border: '1px solid var(--color-gray-300)',
                    borderRadius: 'var(--radius-lg)',
                    background: 'transparent',
                    cursor: 'pointer'
                  }}
                >
                  Try another email
                </button>
              </div>
            ) : (
              /* Form State */
              <>
                {/* Header */}
                <div className="mb-8">
                  <div style={{
                    width: '56px',
                    height: '56px',
                    background: 'linear-gradient(135deg, #1e40af 0%, #2563eb 100%)',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '20px'
                  }}>
                    <Mail className="w-7 h-7 text-white" />
                  </div>
                  <h2 className="text-3xl font-bold mb-2" style={{ color: 'var(--color-gray-900)' }}>
                    Forgot password?
                  </h2>
                  <p className="text-muted">
                    No worries! Enter your email and we'll send you a reset link.
                  </p>
                </div>

                {/* Error Alert */}
                {error && (
                  <div className="mb-6">
                    <Alert type="danger" message={error} onClose={() => setError('')} />
                  </div>
                )}

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Email Input */}
                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm font-semibold mb-2"
                      style={{ color: 'var(--color-gray-700)' }}
                    >
                      Email address
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@company.com"
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        fontSize: '15px',
                        border: '1px solid var(--color-gray-300)',
                        borderRadius: 'var(--radius-lg)',
                        transition: 'all 0.15s ease',
                        color: 'var(--color-gray-900)'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = 'var(--color-primary)';
                        e.target.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = 'var(--color-gray-300)';
                        e.target.style.boxShadow = 'none';
                      }}
                    />
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={loading}
                    className="btn-primary"
                    style={{
                      width: '100%',
                      height: '48px',
                      fontSize: '15px',
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      opacity: loading ? 0.6 : 1,
                      cursor: loading ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {loading ? (
                      <>
                        <LoadingSpinner message="Sending..." size="sm" />
                        <span>Sending reset link...</span>
                      </>
                    ) : (
                      <>
                        <Mail className="w-4 h-4" />
                        <span>Send reset link</span>
                      </>
                    )}
                  </button>
                </form>

                {/* Sign Up Link */}
                <div className="mt-8 text-center">
                  <p className="text-sm" style={{ color: 'var(--color-gray-600)' }}>
                    Don't have an account?{' '}
                    <Link
                      to="/register"
                      className="font-medium"
                      style={{ color: 'var(--color-primary)' }}
                    >
                      Sign up
                    </Link>
                  </p>
                </div>
              </>
            )}
          </div>

          {/* Footer Links */}
          <div className="mt-8 text-center">
            <div className="flex items-center justify-center gap-6 text-sm" style={{ color: 'var(--color-gray-500)' }}>
              <Link to="/privacy" style={{ color: 'var(--color-gray-600)' }}>
                Privacy Policy
              </Link>
              <span>•</span>
              <Link to="/terms" style={{ color: 'var(--color-gray-600)' }}>
                Terms of Service
              </Link>
              <span>•</span>
              <Link to="/contact" style={{ color: 'var(--color-gray-600)' }}>
                Contact Support
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
